import * as React from "react";
import {
    alpha,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import {useTranslation} from "react-i18next";
import i18n from "../../../../i18n";
import {type PayrollCalculation, buildEmployeeSummary} from "../../../../hooks/usePayrollRun";
import {type Contract} from "../../../../services/payrollService";
import {formatPeriodDateRange} from "../../PeriodDateRangeBadge";

interface PayrollRunDetailsPerEmployeeProps {
    open: boolean;
    onClose: () => void;
    employee: PayrollCalculation | null;
    periodLabel: string | null | undefined;
    statusLabel?: string;
    isPeriodOpen?: boolean;
    contract: Contract | null;
    formatEUR: (value: number | null | undefined) => string;
}

function formatDate(value: string | null | undefined): string {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const locale = i18n.language === "al" ? "sq-AL" : "en-GB";
    return d.toLocaleDateString(locale, {day: "2-digit", month: "short", year: "numeric"});
}

// ─── Header bar ───────────────────────────────────────────────────────────────

function HeaderField({label, value}: {readonly label: string; readonly value: React.ReactNode}): React.ReactElement {
    return (
        <Box sx={{minWidth: 0}}>
            <Typography variant="caption" color="text.secondary" sx={{textTransform: "uppercase", letterSpacing: 0.5}}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={700} noWrap>
                {value}
            </Typography>
        </Box>
    );
}

// ─── Summary tab: reconciliation-style cards ───────────────────────────────────

interface LineItem {
    label: string;
    value: string;
    prefix?: "+" | "-";
    bold?: boolean;
}

interface SummaryCardDef {
    number: number;
    title: string;
    accentColor: string;
    lines: LineItem[];
}

function SummaryCard({number, title, accentColor, lines}: SummaryCardDef): React.ReactElement {
    const headerBg = alpha(accentColor, 0.1);
    const footerLine = lines.find((l) => l.bold);
    const bodyLines = lines.filter((l) => !l.bold);

    return (
        <Box
            sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                backgroundColor: "#fffcf8",
                overflow: "hidden",
            }}
        >
            <Box sx={{px: 2, py: 1, backgroundColor: headerBg}}>
                <Typography variant="subtitle2" sx={{fontWeight: 700, color: accentColor}}>
                    {number}. {title}
                </Typography>
            </Box>
            <Box sx={{px: 2, py: 1.25, display: "flex", flexDirection: "column", gap: 0.5}}>
                {bodyLines.map((line) => (
                    <Box key={line.label} sx={{display: "flex", justifyContent: "space-between", gap: 1}}>
                        <Typography variant="body2" color="text.secondary">
                            {line.prefix ? `${line.prefix} ${line.label}` : line.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                            {line.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {footerLine && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: headerBg,
                        borderTop: `1px solid ${alpha(accentColor, 0.3)}`,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" sx={{fontWeight: 700, color: accentColor}}>
                        {`= ${footerLine.label}`}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 700, color: accentColor}}>
                        {footerLine.value}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// ─── Side panels ────────────────────────────────────────────────────────────

function SidePanelRow({label, value, bold}: {readonly label: string; readonly value: string; readonly bold?: boolean}): React.ReactElement {
    return (
        <Box sx={{display: "flex", justifyContent: "space-between", gap: 1}}>
            <Typography variant="body2" color={bold ? "text.primary" : "text.secondary"} fontWeight={bold ? 700 : 400}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={bold ? 700 : 500}>
                {value}
            </Typography>
        </Box>
    );
}

function SidePanel({
    title,
    accentColor,
    children,
}: {
    readonly title: string;
    readonly accentColor?: string;
    readonly children: React.ReactNode;
}): React.ReactElement {
    return (
        <Box sx={{border: "1px solid #ddd", borderRadius: 1, backgroundColor: "#fffcf8", overflow: "hidden"}}>
            <Box sx={{px: 2, py: 1, backgroundColor: accentColor ? alpha(accentColor, 0.1) : "#f5f5f5"}}>
                <Typography variant="subtitle2" sx={{fontWeight: 700, color: accentColor ?? "text.primary"}}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{px: 2, py: 1.25, display: "flex", flexDirection: "column", gap: 0.75}}>
                {children}
            </Box>
        </Box>
    );
}

function EmployeeInfoRow({icon, label, value}: {readonly icon: React.ReactNode; readonly label: string; readonly value: string}): React.ReactElement {
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <Box sx={{color: "text.secondary", display: "flex"}}>{icon}</Box>
            <Box sx={{flexGrow: 1, minWidth: 0}}>
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function PayrollRunDetailsPerEmployee({
    open,
    onClose,
    employee,
    periodLabel,
    statusLabel,
    isPeriodOpen,
    contract,
    formatEUR,
}: PayrollRunDetailsPerEmployeeProps): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    if (!employee) return <></>;

    const summary = buildEmployeeSummary(employee);

    const cards: SummaryCardDef[] = [
        {
            number: 1,
            title: t('payroll.run.details.title.earnings'),
            accentColor: theme.palette.success.main,
            lines: [
                {label: t('payroll.run.reconciliation.baseGross'), value: formatEUR(summary.earnings.baseGross)},
                {label: t('payroll.run.reconciliation.grossAdditions'), prefix: "+", value: formatEUR(summary.earnings.grossAdditions)},
                {label: t('payroll.run.reconciliation.grossDeductions'), prefix: "-", value: formatEUR(summary.earnings.grossDeductions)},
                {label: t('payroll.run.reconciliation.netToGrossAdjustment'), prefix: "+", value: formatEUR(summary.earnings.netToGrossAdjustment)},
                {label: t('payroll.run.table.header.finalGross'), bold: true, value: formatEUR(summary.earnings.finalGross)},
            ],
        },
        {
            number: 2,
            title: t('payroll.run.details.title.statutoryDeductions'),
            accentColor: theme.palette.info.main,
            lines: [
                {
                    label: t('payroll.run.details.employeePensionPct', {pct: summary.statutoryDeductions.pensionEmployeePct}),
                    prefix: "-",
                    value: formatEUR(summary.statutoryDeductions.pensionEmployee),
                },
                {label: t('payroll.run.summary.incomeTax'), prefix: "-", value: formatEUR(summary.statutoryDeductions.incomeTax)},
                {label: t('payroll.run.details.totalStatutoryDeductions'), bold: true, value: formatEUR(summary.statutoryDeductions.totalStatutoryDeductions)},
            ],
        },
        {
            number: 3,
            title: t('payroll.run.details.title.otherAdjustments'),
            accentColor: theme.palette.secondary.main,
            lines: [
                {label: t('payroll.run.reconciliation.netAdditions'), prefix: "+", value: formatEUR(summary.otherAdjustments.netAdditions)},
                {label: t('payroll.run.reconciliation.netDeductions'), prefix: "-", value: formatEUR(summary.otherAdjustments.netDeductions)},
                {label: t('payroll.run.reconciliation.familyInsurance'), prefix: "-", value: formatEUR(summary.otherAdjustments.familyInsurance)},
                {label: t('payroll.run.reconciliation.voluntaryFund'), prefix: "-", value: formatEUR(summary.otherAdjustments.voluntaryFund)},
                {label: t('payroll.run.details.totalOtherDeductions'), bold: true, value: formatEUR(summary.otherAdjustments.totalOtherDeductions)},
            ],
        },
        {
            number: 4,
            title: t('payroll.run.details.title.netPay'),
            accentColor: theme.palette.warning.main,
            lines: [
                {label: t('payroll.run.reconciliation.netBeforeOtherDeductions'), prefix: "-", value: formatEUR(summary.netPay.netBeforeOtherDeductions)},
                {label: t('payroll.run.details.totalOtherDeductions'), prefix: "-", value: formatEUR(summary.netPay.totalOtherDeductions)},
                {label: t('payroll.run.table.header.netToBank'), bold: true, value: formatEUR(summary.netPay.netToBank)},
            ],
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={() => {}}
            disableEscapeKeyDown
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
        >
            {/* Header bar */}
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    alignItems: "center",
                    borderBottom: "1px solid #eee",
                    backgroundColor: "#fafafa",
                }}
            >
                <HeaderField label={t('payroll.run.table.header.employeeNo')} value={employee.employeeNumber}/>
                <HeaderField label={t('payroll.run.details.employeeName')} value={employee.name}/>
                <HeaderField label={t('hr.jobs.table.header.branch')} value={employee.branch}/>
                <HeaderField label={t('payroll.run.details.payrollPeriod')} value={periodLabel ? formatPeriodDateRange(periodLabel) : "—"}/>
                <Box sx={{ml: "auto"}}>
                    <Chip
                        label={statusLabel ?? "—"}
                        size="small"
                        color={isPeriodOpen ? "success" : "default"}
                        sx={{fontWeight: 700}}
                    />
                </Box>
            </Box>

            <DialogContent sx={{backgroundColor: "#f7f7f5"}}>
                {(
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: {xs: "column", md: "row"},
                            gap: 2,
                            alignItems: "flex-start",
                        }}
                    >
                        {/* Left: 4 numbered reconciliation cards */}
                        <Box sx={{flex: "2 1 0", width: "100%", display: "flex", flexDirection: "column", gap: 2}}>
                            {cards.map((card) => (
                                <SummaryCard key={card.number} {...card} />
                            ))}
                        </Box>

                        {/* Right: Key Results / Employer Cost / Employee Info panels */}
                        <Box sx={{flex: "1 1 0", width: "100%", display: "flex", flexDirection: "column", gap: 2}}>
                            <SidePanel title={t('payroll.run.details.keyResults')}>
                                <SidePanelRow label={t('payroll.run.table.header.finalGross')} value={formatEUR(summary.earnings.finalGross)}/>
                                <SidePanelRow label={t('payroll.run.reconciliation.employeePension')} value={formatEUR(summary.statutoryDeductions.pensionEmployee)}/>
                                <SidePanelRow label={t('payroll.run.summary.incomeTax')} value={formatEUR(summary.statutoryDeductions.incomeTax)}/>
                                <SidePanelRow label={t('payroll.run.table.header.otherDeductions')} value={formatEUR(summary.otherAdjustments.totalOtherDeductions)}/>
                                <Divider sx={{my: 0.5}}/>
                                <SidePanelRow label={t('payroll.run.table.header.netToBank')} value={formatEUR(summary.netPay.netToBank)} bold/>
                            </SidePanel>

                            <SidePanel title={t('payroll.run.details.employerCostTitle')} accentColor={theme.palette.warning.main}>
                                <SidePanelRow label={t('payroll.run.table.header.finalGross')} value={formatEUR(summary.employerCost.finalGross)}/>
                                <SidePanelRow
                                    label={t('payroll.run.details.employerPensionPct', {pct: summary.employerCost.employerPensionPct})}
                                    value={formatEUR(summary.employerCost.employerPension)}
                                />
                                <SidePanelRow label={t('payroll.run.reconciliation.employerPaidBenefits')} value={formatEUR(summary.employerCost.employerPaidBenefits)}/>
                                <Divider sx={{my: 0.5}}/>
                                <SidePanelRow label={t('payroll.run.summary.totalEmployerCost')} value={formatEUR(summary.employerCost.totalEmployerCost)} bold/>
                            </SidePanel>

                            <SidePanel title={t('payroll.run.details.employeeInfoTitle')}>
                                <EmployeeInfoRow icon={<BadgeIcon fontSize="small"/>} label={t('payroll.run.details.idPersonalNo')} value={contract?.personalNo || "—"}/>
                                <EmployeeInfoRow icon={<WorkOutlineIcon fontSize="small"/>} label={t('employee.contract.field.jobPosition')} value={contract?.jobPosition || "—"}/>
                                <EmployeeInfoRow icon={<AccessTimeIcon fontSize="small"/>} label={t('employee.form.field.employmentType')} value={contract?.employmentType || "—"}/>
                                <EmployeeInfoRow icon={<AccessTimeIcon fontSize="small"/>} label={t('employee.contract.field.workload')} value={contract?.workload ? `${contract.workload}%` : "—"}/>
                                <EmployeeInfoRow icon={<DescriptionIcon fontSize="small"/>} label={t('payroll.run.details.contractLabel')} value={contract?.contractNumber || "—"}/>
                                <EmployeeInfoRow icon={<EventIcon fontSize="small"/>} label={t('payroll.run.details.contractStart')} value={contract ? formatDate(contract.startDate) : "—"}/>
                                <EmployeeInfoRow icon={<EventIcon fontSize="small"/>} label={t('payroll.run.details.contractEnd')} value={contract?.endDate ? formatDate(contract.endDate) : t('payroll.run.details.noEndDate')}/>
                            </SidePanel>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2, borderTop: "1px solid #eee"}}>
                <Button onClick={onClose} startIcon={<ArrowBackIcon/>}>
                    {t('payroll.run.details.backButton')}
                </Button>
                <Box sx={{flexGrow: 1}}/>
                <Tooltip title={t('common.notAvailableYet')}>
                    <span>
                        <Button variant="outlined" startIcon={<FileDownloadIcon/>} disabled>
                            {t('payroll.run.details.downloadPayslip')}
                        </Button>
                    </span>
                </Tooltip>
                <Button onClick={onClose} variant="contained">
                    {t('common.actions.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
